<template>
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
  <NotificationToaster />
  <Toast />
  <ConfirmDialog />
  <RequiredNameDetailsDialog
    v-if="memberNeedsNameUpdate"
    :member="currentMember!"
  />
  <RequiredEmergencyContactDialog
    v-else-if="memberNeedsEmergencyContact"
    :member="currentMember!"
  />
  <RequiredSubkreweTermsDialog
    v-else-if="memberNeedsSubkreweTermsAcceptance"
    :member="currentMember!"
    :subkrewe="currentMember!.subkrewe!"
  />
</template>

<script setup lang="ts">
import { useCurrentUser } from 'vuefire'
import { NotificationToaster } from '~/components/notifications'
import RequiredNameDetailsDialog from '~/components/dialogs/RequiredNameDetails.vue'
import RequiredEmergencyContactDialog from '~/components/dialogs/RequiredEmergencyContact.vue'
import RequiredSubkreweTermsDialog from '~/components/dialogs/RequiredSubkreweTerms.vue'
import { memberNeedsToAcceptTerms } from '~/shared/utils/subkreweTerms.js'

const router = useRouter()
const route = useRoute()
const user = useCurrentUser()
const membersStore = useMembersStore()

// Get current member reactively from store
const currentMember = computed(() => {
  if (!user.value?.uid) return null
  return membersStore.members[`members/${user.value.uid}`] || null
})

// Check if member needs to update their name
const memberNeedsNameUpdate = computed(() => {
  if (!currentMember.value) return false
  // Not while signing up: its first step asks for the name (an email
  // account starts without one)
  if (!currentMember.value.member_type || currentMember.value.member_type === 'pending') {
    return false
  }
  const { first_name, last_name } = currentMember.value
  const needs = !first_name?.trim() || !last_name?.trim()
  console.warn('[App] memberNeedsNameUpdate check:', { first_name, last_name, needs })
  return needs
})

// Check if member needs to provide emergency contact
const memberNeedsEmergencyContact = computed(() => {
  if (!currentMember.value) return false

  // Don't prompt pending members (haven't completed signup yet)
  if (!currentMember.value.member_type || currentMember.value.member_type === 'pending') {
    return false
  }

  const { emergency_contact, emergency_contacts } = currentMember.value

  // Check new array format first
  if (emergency_contacts && emergency_contacts.length > 0) {
    // A missing first contact counts as incomplete, same as a blank one.
    const first = emergency_contacts[0]
    return !first?.name?.trim() || !first?.phone?.trim()
  }

  // Fall back to legacy single contact
  const needs = !emergency_contact?.name?.trim() || !emergency_contact?.phone?.trim()
  return needs
})

// Check if member needs to accept SubKrewe-specific Terms & Conditions
// (captain of the SubKrewe is exempt to avoid locking themselves out of editing).
const memberNeedsSubkreweTermsAcceptance = computed(() => {
  const m = currentMember.value
  if (!m) return false
  if (!m.member_type || m.member_type === 'pending') return false

  const subkrewe = m.subkrewe
  if (!subkrewe) return false

  // Captains of this SubKrewe are exempt — they author the terms.
  if (subkrewe.captains?.some(c => c?.id === m.id)) return false

  return memberNeedsToAcceptTerms(m, subkrewe)
})

// Use the centralized core subscriptions manager
const { setupCoreSubscriptions, getSubscriptionDiagnostics } = useCoreSubscriptions()

// Track whether we're actually forcing a refresh
const isForceRefresh = ref(false)

// Function to expose for DevTools to force subscription refresh
const refreshCoreSubscriptions = async () => {
  isForceRefresh.value = true
  console.warn('[App] Manual refresh of core subscriptions requested')
  try {
    return await setupCoreSubscriptions()
  }
  finally {
    isForceRefresh.value = false
  }
}

// Handle navigation after login/logout
watch(user, (newUser, prevUser) => {
  if (prevUser && !newUser) {
    // User logged out
    router.push('/login')
  }
  else if (newUser && typeof route.query.redirect === 'string') {
    // User logged in with redirect query
    router.push(route.query.redirect)
  }
})

// Expose the refresh function for DevTools
defineExpose({
  setupCoreSubscriptions: refreshCoreSubscriptions,
  getSubscriptionDiagnostics,
})
</script>
